import { track } from "@/lib/analytics"
import { useMemo, useState } from "react"
import { flushThreadEdits } from "./edits"
import { downloadOiaSimDocx } from "./lib/exportDocx"
import { canonicalIds, fullTitles, planNumbering, sectionHeadings, stripLeadingHeading } from "./sections"
import { SectionEditor } from "./SectionEditor"
import { useSimStore } from "./store"
import "./oia-sim.css"

/** Canonical section ids for the case, how many have text, and whether drafting has begun (any section exists, or one is streaming). */
export function useDraftSections(threadId: string) {
  const draft = useSimStore((s) => s.threads[threadId]?.draft ?? null)
  const streaming = useSimStore((s) => s.threads[threadId]?.streamingSection ?? null)
  const ids = useMemo(
    () => canonicalIds([...Object.keys(draft?.sections ?? {}), ...(streaming ? [streaming.sectionId] : [])]),
    [draft, streaming],
  )
  const drafted = ids.filter((id) => draft?.sections[id]?.markdown.trim()).length
  const started = Object.keys(draft?.sections ?? {}).length > 0 || streaming !== null
  return { draft, streaming, ids, drafted, started }
}

/** Export .docx: pending edits saved first, so the file matches the page. */
export function useDraftExport(threadId: string, title: string) {
  const facts = useSimStore((s) => s.threads[threadId]?.caseFacts ?? null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState("")
  const download = async () => {
    setExporting(true)
    setExportError("")
    try {
      // Pending edits are saved first so the file matches what is on screen.
      const outcome = await flushThreadEdits(threadId)
      if (outcome === "conflict" || outcome === "error") {
        setExportError("Some edits are not saved yet. Resolve them, then download again.")
        return
      }
      const latest = useSimStore.getState().threads[threadId]?.draft
      // Every section id is passed (empty ones too) so the numbering matches the page; empty ones are skipped in the file.
      const all = canonicalIds(Object.keys(latest?.sections ?? {}))
      if (!all.some((id) => latest?.sections[id]?.markdown.trim())) return
      await downloadOiaSimDocx(
        all.map((id) => ({ id, markdown: latest?.sections[id]?.markdown ?? "" })),
        {
          title: title || "OIA draft",
          titles: latest?.titles,
          party: facts?.appellant_name?.trim() || facts?.taxpayer_name?.trim() || undefined,
        },
      )
      track("draft_exported")
    } catch {
      setExportError("Couldn't build the DOCX. Try again.")
    } finally {
      setExporting(false)
    }
  }
  return { download, exporting, exportError }
}

/**
 * The draft as one paper document: every section in order as its own editor (autosave, versions,
 * conflicts and history unchanged). The caption is the code-built caption section, not a block of
 * its own. `aside` lets a page tie its own notes to a section as it renders.
 */
export function DraftPaper({ threadId, title, aside }: { threadId: string; title: string; aside?: (sectionId: string) => React.ReactNode }) {
  const { draft, streaming, ids } = useDraftSections(threadId)
  const titles = useMemo(() => fullTitles(draft?.titles, draft?.sections.points?.markdown), [draft])
  const headings = useMemo(() => sectionHeadings(ids, titles, (id) => !!draft?.sections[id]?.markdown.trim()), [ids, titles, draft])
  // One paragraph counter across the page; the editors stay unnumbered and the gutter draws the numbers.
  const numbering = useMemo(
    () => planNumbering(ids.map((id) => ({ id, markdown: stripLeadingHeading(draft?.sections[id]?.markdown ?? "") }))),
    [ids, draft],
  )
  return (
    <article className="osia-paper" aria-label={title ? `${title}: the draft order` : "Draft order document"}>
      {ids.map((id, i) => {
        const group = headings[id]?.group
        const note = aside?.(id)
        return (
          <div key={id} id={`osia-sec-${id}`} className={`osia-doc-sec${note ? " has-note" : ""}`}>
            <SectionEditor
              key={`${threadId}:${id}`}
              threadId={threadId}
              sectionId={id}
              section={draft?.sections[id]}
              streaming={streaming?.sectionId === id ? streaming.markdown : null}
              heading={headings[id]}
              groupHeading={!!group && headings[ids[i - 1]]?.group !== group}
              ids={ids}
              titles={titles}
              numbers={numbering.get(id)}
            />
            {note ? <aside className="nsdr-margin">{note}</aside> : null}
          </div>
        )
      })}
    </article>
  )
}
